import { InjectionToken } from '@angular/core';

/**
 * Configuration interface for User Management Library
 * 
 * @remarks
 * This configuration is required for the library to function properly.
 * Provide this configuration using the providedUserManagementLib() function
 * or by manually providing the USER_MANAGEMENT_API_CONFIG token.
 */
export interface UserManagementConfig {
  /**
   * Base URL for the User Management API
   * 
   * @example 'http://localhost:3001'
   * @example 'https://api.example.com'
   * 
   * @remarks
   * Should NOT include trailing slash or /api path.
   * The library will append specific API paths like /api/users, /api/organizations, etc.
   */
  apiBaseUrl: string;

  /**
   * Optional: Enable debug logging
   * 
   * @default false
   * 
   * @remarks
   * When enabled, service methods will log HTTP requests and responses
   * to the browser console. Useful during development but should be
   * disabled in production.
   */
  debug?: boolean;

  /**
   * Optional: Request timeout in milliseconds
   * 
   * @default 30000 (30 seconds)
   * 
   * @remarks
   * HTTP requests will be automatically cancelled if they exceed this duration.
   * Set to 0 to disable timeout.
   */
  timeout?: number;
}

/**
 * Injection token for User Management API configuration
 * 
 * @remarks
 * Use this token to provide configuration to the library.
 * 
 * @example
 * **Using the provider function (recommended):**
 * ```typescript
 * import { provideUserManagementLib } from 'user-management-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideUserManagementLib({
 *       apiBaseUrl: environment.apiBaseUrl,
 *       debug: !environment.production
 *     })
 *   ]
 * };
 * ```
 * 
 * @example
 * **Manual configuration:**
 * ```typescript
 * import { USER_MANAGEMENT_API_CONFIG } from 'user-management-lib';
 * 
 * providers: [
 *   {
 *     provide: USER_MANAGEMENT_API_CONFIG,
 *     useValue: {
 *       apiBaseUrl: 'https://api.example.com',
 *       debug: false
 *     }
 *   }
 * ]
 * ```
 */
export const USER_MANAGEMENT_API_CONFIG = new InjectionToken<UserManagementConfig>(
  'USER_MANAGEMENT_API_CONFIG',
  {
    providedIn: 'root',
    factory: () => {
      // Default configuration - should be overridden by consuming app
      console.warn(
        'USER_MANAGEMENT_API_CONFIG not provided. Using default configuration. ' +
        'Please provide configuration using provideUserManagementLib() or by providing USER_MANAGEMENT_API_CONFIG token.'
      );
      return {
        apiBaseUrl: 'http://localhost:3001',
        debug: false,
        timeout: 30000
      };
    }
  }
);
